import * as React from 'react';
import { EMPTY_REP_CARD_FILTERS, type RepCardFilters } from './repCardFilters';

const STORAGE_KEY = 'ai-coaching-review:rep-card-filters';

type FiltersByRepId = Record<string, RepCardFilters>;

const readMap = (): FiltersByRepId => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as FiltersByRepId) : {};
	} catch {
		return {};
	}
};

/**
 * One rep card's filter values, persisted to `localStorage` keyed by rep id so each card keeps its own
 * score/length/date selections across remounts (opening a call replaces the grid) and reloads.
 *
 * Writes re-read the stored map before merging: every card shares one storage key, so merging into a
 * snapshot captured at mount would clobber sibling cards' entries.
 */
export function usePersistedRepCardFilters(repId: string) {
	const [filters, setFiltersState] = React.useState<RepCardFilters>(() => {
		const stored = readMap()[repId];
		return stored ? { ...EMPTY_REP_CARD_FILTERS, ...stored } : EMPTY_REP_CARD_FILTERS;
	});

	const setFilters = React.useCallback(
		(next: RepCardFilters) => {
			setFiltersState(next);
			try {
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readMap(), [repId]: next }));
			} catch {
				// Ignore write failures (private mode / storage disabled) — in-memory state still updates.
			}
		},
		[repId]
	);

	return [filters, setFilters] as const;
}
